import { z } from 'zod';
import { ORG_POLL_INTERVAL_MAX_S, ORG_POLL_INTERVAL_MIN_S, PLANS, ROLES, SCOPE_TYPES } from '../constants.ts';
import { idSchema, unixMsSchema } from './common.ts';

export const roleSchema = z.enum(ROLES);
export const planSchema = z.enum(PLANS);

// --- Member scopes (Sprint 7) ---------------------------------------------------
// One scope entry restricting an editor/viewer to a screen or group (docs/02, docs/08).
export const memberScopeSchema = z.object({
  scope_type: z.enum(SCOPE_TYPES),
  scope_id: idSchema,
});
export type MemberScope = z.infer<typeof memberScopeSchema>;

// PUT /v1/org/members/:userId/scopes — the full replacement scope set. `null` (or an
// empty array) clears the member to org-wide access. Ids are validated org-owned in the
// service; non-empty scopes are rejected for owners/admins (they are always org-wide).
export const setMemberScopesSchema = z.object({
  scopes: z.array(memberScopeSchema).nullable(),
});
export type SetMemberScopes = z.infer<typeof setMemberScopesSchema>;

// Shape stored in orgs.limits_json.
export const planLimitsSchema = z.object({
  max_screens: z.number().int().nonnegative(),
  max_storage_mb: z.number().int().nonnegative(),
  assistant: z.boolean(),
  // Monthly AI generation cap (docs/09 §B). Optional: absent → GEN_MONTHLY_DEFAULT.
  gen_monthly: z.number().int().nonnegative().optional(),
  // Org-wide default poll cadence override (Sprint 11 T3, docs/04 §Traffic diet). Optional:
  // absent → DEFAULT_POLL_INTERVAL_S.
  poll_interval_s: z.number().int().min(ORG_POLL_INTERVAL_MIN_S).max(ORG_POLL_INTERVAL_MAX_S).optional(),
});
export type PlanLimitsSchema = z.infer<typeof planLimitsSchema>;

export const orgSchema = z.object({
  id: idSchema,
  name: z.string().min(1),
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, 'slug must be lowercase alphanumeric with dashes'),
  plan: planSchema,
  limits: planLimitsSchema,
  // Org-wide default overlay (docs/09 §C2); null when unset. A screen's own overlay wins.
  default_overlay_id: idSchema.nullable(),
  created_at: unixMsSchema,
});
export type Org = z.infer<typeof orgSchema>;

// Per-org monthly LLM token telemetry (docs/05 §cost; expert panel move #7). Accumulated
// in KV as the assistant loop runs and surfaced read-only in GET /v1/org and the superadmin
// org list. `month` is the 'YYYY-MM' bucket the counters belong to. Optional/additive.
export const llmUsageSchema = z.object({
  input: z.number().int().nonnegative(),
  output: z.number().int().nonnegative(),
  month: z.string(),
});
export type LlmUsage = z.infer<typeof llmUsageSchema>;

// GET /v1/org → current org + plan + limits + usage.
export const orgUsageSchema = z.object({
  screens: z.number().int().nonnegative(),
  storage_mb: z.number().nonnegative(),
  // Monthly assistant token usage; absent on surfaces that don't attach it.
  llm_tokens: llmUsageSchema.optional(),
});
export type OrgUsage = z.infer<typeof orgUsageSchema>;

export const orgWithUsageSchema = orgSchema.extend({ usage: orgUsageSchema });
export type OrgWithUsage = z.infer<typeof orgWithUsageSchema>;

// POST /v1/org/invites
export const createInviteSchema = z.object({
  email: z.string().email(),
  role: roleSchema,
  // Optional scoped access carried into the membership on accept (Sprint 7). Ignored
  // for owner/admin invites (they are always org-wide). Ids validated org-owned.
  scopes: z.array(memberScopeSchema).optional(),
});
export type CreateInvite = z.infer<typeof createInviteSchema>;

// POST /v1/auth/accept-invite — invite-only registration. Email comes from the
// invite, never the body.
export const acceptInviteSchema = z.object({
  token: z.string().min(1),
  name: z.string().min(1),
  password: z.string().min(8),
});
export type AcceptInvite = z.infer<typeof acceptInviteSchema>;

// POST /v1/admin/orgs — superadmin creates an org + its owner invite.
export const adminCreateOrgSchema = z.object({
  name: z.string().min(1),
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, 'slug must be lowercase alphanumeric with dashes'),
  plan: planSchema.optional(),
  limits: planLimitsSchema,
  owner_email: z.string().email(),
});
export type AdminCreateOrg = z.infer<typeof adminCreateOrgSchema>;

// PATCH /v1/admin/orgs/:id — superadmin updates plan/limits.
export const adminUpdateOrgSchema = z
  .object({
    plan: planSchema.optional(),
    limits: planLimitsSchema.optional(),
  })
  .refine((v) => v.plan !== undefined || v.limits !== undefined, {
    message: 'provide plan and/or limits',
  });
export type AdminUpdateOrg = z.infer<typeof adminUpdateOrgSchema>;

// PATCH /v1/org — org settings (admin+): display name, the org-wide default overlay
// (docs/09 §C2), and/or the default poll cadence (Sprint 11 T3, docs/04 §Traffic diet).
// `default_overlay_id` null clears it (validated org-owned in the service);
// `poll_interval_s` null clears back to DEFAULT_POLL_INTERVAL_S. At least one field
// must be present.
export const updateOrgSchema = z
  .object({
    name: z.string().min(1).optional(),
    default_overlay_id: idSchema.nullish(),
    poll_interval_s: z.number().int().min(ORG_POLL_INTERVAL_MIN_S).max(ORG_POLL_INTERVAL_MAX_S).nullish(),
  })
  .refine(
    (v) => v.name !== undefined || v.default_overlay_id !== undefined || v.poll_interval_s !== undefined,
    { message: 'provide name, default_overlay_id, and/or poll_interval_s' },
  );
export type UpdateOrg = z.infer<typeof updateOrgSchema>;

// Superadmin org list row: org + usage + owner email + member count.
export const adminOrgListItemSchema = orgSchema.extend({
  usage: orgUsageSchema,
  owner_email: z.string().nullable(),
  member_count: z.number().int().nonnegative(),
});
export type AdminOrgListItem = z.infer<typeof adminOrgListItemSchema>;

// POST /v1/admin/orgs response — the created org + the owner invite link.
export const createAdminOrgResultSchema = z.object({
  org: adminOrgListItemSchema,
  invite_url: z.string(),
});
export type CreateAdminOrgResult = z.infer<typeof createAdminOrgResultSchema>;

// POST /v1/org/invites response — the shareable link back.
export const inviteResultSchema = z.object({
  id: idSchema,
  email: z.string().email(),
  role: roleSchema,
  invite_url: z.string(),
  expires_at: unixMsSchema,
});
export type InviteResult = z.infer<typeof inviteResultSchema>;

// GET /v1/org/members — team roster + pending invites.
export const orgMemberSchema = z.object({
  user_id: idSchema,
  email: z.string().email(),
  name: z.string(),
  role: roleSchema,
  is_you: z.boolean(),
  // Scoped access (Sprint 7): null = org-wide (owners/admins always; editors/viewers
  // with no scope rows). Non-null = the screen/group ids this member's writes are limited to.
  scopes: z.array(memberScopeSchema).nullable(),
});
export type OrgMember = z.infer<typeof orgMemberSchema>;

export const pendingInviteSchema = z.object({
  id: idSchema,
  email: z.string().email(),
  role: roleSchema,
  invite_url: z.string(),
  expires_at: unixMsSchema,
});
export type PendingInvite = z.infer<typeof pendingInviteSchema>;

export const teamResultSchema = z.object({
  members: z.array(orgMemberSchema),
  invites: z.array(pendingInviteSchema),
});
export type TeamResult = z.infer<typeof teamResultSchema>;

// Personal API keys (docs/08). Secret returned exactly once on create.
export const apiKeySchema = z.object({
  id: idSchema,
  name: z.string(),
  prefix: z.string(),
  created_at: unixMsSchema,
  last_used_at: unixMsSchema.nullable(),
});
export type ApiKey = z.infer<typeof apiKeySchema>;

export const createApiKeySchema = z.object({ name: z.string().min(1) });
export type CreateApiKey = z.infer<typeof createApiKeySchema>;

export const createdApiKeySchema = z.object({
  key: apiKeySchema,
  secret: z.string(),
});
export type CreatedApiKey = z.infer<typeof createdApiKeySchema>;

// GET /v1/me → user + orgs + role
export const meSchema = z.object({
  user: z.object({
    id: idSchema,
    email: z.string().email(),
    name: z.string(),
    is_superadmin: z.boolean(),
  }),
  orgs: z.array(
    z.object({
      id: idSchema,
      name: z.string(),
      slug: z.string(),
      role: roleSchema,
    }),
  ),
});
export type Me = z.infer<typeof meSchema>;
